import { summarize, checkRowCeiling, type IngestAnalysis, type IngestDraft } from "@/lib/ingest";

const PENDING_RESUME_DRAFT_KEY = "mapize.pendingResumeDraft";

interface SessionStorageLike {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

/**
 * FAIL-485 — pure decision logic for resuming an anonymous draft after a
 * sign-in redirect, split out of IngestEditor's mount effect so it's unit-
 * testable without rendering the whole editor (matches the checkoutReturn.ts
 * pattern). A signed-out user who builds a map and clicks Save was bounced to
 * /sign-in with no return state — the draft was already being saved to
 * IndexedDB (saveDraft), but nothing ever read it back (loadDraft/listDrafts
 * had zero callers anywhere).
 */

/** The sign-in return trip: `/?resume_draft=<id>`. */
export function resumeDraftId(search: string): string | null {
  return new URLSearchParams(search).get("resume_draft");
}

/** Auth-only restoration that must not auto-save before checkout completes. */
export function restoreDraftId(search: string): string | null {
  return new URLSearchParams(search).get("restore_draft");
}

/**
 * Keep the interrupted Save in the current browser tab as a fallback for auth
 * providers that discard Clerk's post-auth query string during an SSO callback.
 * The map body remains in IndexedDB; this stores only its opaque draft id.
 */
export function rememberResumeDraft(storage: SessionStorageLike, draftId: string): void {
  storage.setItem(PENDING_RESUME_DRAFT_KEY, draftId);
}

/** Read and clear the tab-scoped fallback so an interrupted Save resumes once. */
export function takeRememberedResumeDraft(storage: SessionStorageLike): string | null {
  const draftId = storage.getItem(PENDING_RESUME_DRAFT_KEY);
  storage.removeItem(PENDING_RESUME_DRAFT_KEY);
  return draftId;
}

/** The same query string with `resume_draft` stripped, for history.replaceState. */
export function stripResumeDraftParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("resume_draft");
  params.delete("restore_draft");
  return params.toString();
}

/**
 * Reconstruct an IngestAnalysis from a saved draft. `header`/`headerRowIndex`
 * aren't persisted in IngestDraft (only the post-analysis structured data is —
 * see draft.ts) — they feed only the row-review grid's display, so a header
 * row of 0 is a reasonable default rather than a functional loss. `summary`/
 * `rowCeiling` are cheaply recomputed from the real restored rows.
 */
export function analysisFromDraft(draft: IngestDraft): IngestAnalysis {
  return {
    headerRowIndex: 0,
    header: [],
    profiles: draft.profiles,
    mapping: draft.mapping,
    rows: draft.rows,
    countryDefault: draft.country_default,
    summary: summarize(draft.rows),
    rowCeiling: checkRowCeiling(draft.rows.length),
  };
}
